import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import { clearAllCaches, forceRefresh, checkForUpdates } from '../utils/pwaUtils';

const PWADebugTools: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isClearing, setIsClearing] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const toast = useToast();

  const handleClearCache = async () => {
    setIsClearing(true);
    try {
      await clearAllCaches();
      toast({
        title: 'Cache Cleared',
        description: 'All caches have been cleared successfully',
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to clear cache',
        status: 'error',
        duration: 3000
      });
    } finally {
      setIsClearing(false);
    }
  };

  const handleForceRefresh = () => {
    toast({
      title: 'Refreshing App',
      description: 'Clearing all data and reloading...',
      status: 'info',
      duration: 2000
    });
    setTimeout(() => {
      forceRefresh();
    }, 2000);
  };

  const handleCheckUpdates = async () => {
    setIsChecking(true);
    try {
      const hasUpdates = await checkForUpdates();
      toast({
        title: hasUpdates ? 'Updates Found' : 'No Updates',
        description: hasUpdates ? 'App will reload with updates' : 'You have the latest version',
        status: hasUpdates ? 'success' : 'info',
        duration: 3000
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to check for updates',
        status: 'error',
        duration: 3000
      });
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <>
      <Tooltip label="PWA Debug Tools" placement="left">
        <IconButton
          aria-label="PWA Debug Tools"
          icon={<SettingsIcon />}
          size="sm"
          variant="ghost"
          position="fixed"
          bottom="20px"
          right="20px"
          zIndex={1000}
          bg="rgba(255, 255, 255, 0.9)"
          onClick={onOpen}
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>PWA Debug Tools</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Use these tools if you experience white screens or loading issues:
                </Text>
              </Box>
              
              <Button
                colorScheme="blue"
                variant="outline"
                onClick={handleClearCache}
                isLoading={isClearing}
                loadingText="Clearing..."
              >
                Clear All Cache
              </Button>
              
              <Button
                colorScheme="orange"
                variant="outline"
                onClick={handleCheckUpdates}
                isLoading={isChecking}
                loadingText="Checking..."
              >
                Check for Updates
              </Button>
              
              <Button
                colorScheme="red"
                variant="outline"
                onClick={handleForceRefresh}
              >
                Force Refresh App
              </Button>
              
              <Text fontSize="xs" color="gray.500">
                • Clear Cache: Removes stored data that might be causing issues<br/>
                • Check Updates: Looks for new app versions<br/>
                • Force Refresh: Clears everything and reloads the app
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PWADebugTools;
